import React, { useEffect, useState } from 'react';
import { db, auth } from '../firebaseConfig';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { updatePassword } from 'firebase/auth';
import './EditUsers.css';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const EditUsers = () => {
  const [customers, setCustomers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [filteredAdmins, setFilteredAdmins] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    handleSearch(searchTerm);
  }, [searchTerm, customers, admins]);

  const fetchUsers = async () => {
    try {
      const customersSnap = await getDocs(collection(db, 'customers'));
      const adminsSnap = await getDocs(collection(db, 'admin_users'));
  
      const customerDocs = customersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const adminDocs = adminsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  
      const currentUser = auth.currentUser;
      if (!currentUser || !currentUser.email) {
        console.error("🚨 No authenticated user or missing email.");
        setMessage(t('update_failed'));
        return;
      }
  
      const currentAdmin = adminDocs.find(admin => admin.email === currentUser.email);
      const currentUserRole = currentAdmin?.role;
      const currentShopId = currentAdmin?.coffee_shop_id;
  
      const visibleAdmins = adminDocs.filter(admin => {
        if (currentUserRole === 'system_admin') return true;
        if (admin.role === 'system_admin') return false;
        return admin.coffee_shop_id === currentShopId;
      });
  
      const visibleCustomers = customerDocs.filter(customer => {
        if (currentUserRole === 'system_admin') return true;
        return customer.coffee_shop_id === currentShopId;
      });
  
      setCustomers(visibleCustomers);
      setAdmins(visibleAdmins);
      setFilteredCustomers(visibleCustomers);
      setFilteredAdmins(visibleAdmins);
    } catch (error) {
      console.error('Error fetching users:', error);
      setMessage(t('update_failed'));
    }
  };
  const handleSearch = (term) => {
    const lowerTerm = term.toLowerCase();

    setFilteredCustomers(customers.filter(cust =>
      (cust.name?.toLowerCase() || '').includes(lowerTerm) ||
      (cust.email?.toLowerCase() || '').includes(lowerTerm) ||
      (cust.organization?.toLowerCase() || '').includes(lowerTerm)
    ));

    setFilteredAdmins(admins.filter(admin =>
      (admin.username?.toLowerCase() || '').includes(lowerTerm) ||
      (admin.email?.toLowerCase() || '').includes(lowerTerm) ||
      (admin.role?.toLowerCase() || '').includes(lowerTerm)
    ));
  };

  const handleCustomerInputChange = (id, field, value) => {
    setFilteredCustomers(filteredCustomers.map(cust =>
      cust.id === id ? { ...cust, [field]: value } : cust
    ));
  };

  const handleAdminInputChange = (id, field, value) => {
    setFilteredAdmins(filteredAdmins.map(admin =>
      admin.id === id ? { ...admin, [field]: value } : admin
    ));
  };

  const submitCustomerChanges = async (id) => {
    const customer = filteredCustomers.find(cust => cust.id === id);
    await updateDoc(doc(db, 'customers', id), {
      name: customer.name,
      email: customer.email,
      phone: customer.phone,
      organization: customer.organization,
    });
    setMessage(t('customer_update_success'));
    fetchUsers();
  };

  const submitAdminChanges = async (id) => {
    const admin = filteredAdmins.find(adm => adm.id === id);

    await updateDoc(doc(db, 'admin_users', id), {
      username: admin.username,
      email: admin.email,
      role: admin.role,
      pin: admin.pin,
      location: admin.location || "",
    });

    if (admin.newPassword && admin.newPassword.length >= 6) {
      const authUser = auth.currentUser;
      if (authUser && authUser.uid === id) {
        await updatePassword(authUser, admin.newPassword);
      } else {
        alert("User must log in to update their password.");
      }
    }

    setMessage(t('update_success'));
    fetchUsers();
  };

  return (
    <div className="edit-users-container">
      <h1>{t('edit_users_title')}</h1>

      <div className="dashboard-btn-container">
        <button className="dashboard-btn" onClick={() => navigate('/dashboard')}>
          {t('back_to_dashboard')}
        </button>
      </div>

      <input
        className="search-field"
        type="text"
        placeholder={t('search_users')}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      {message && <div className="message success">{message}</div>}

      <h2>{t('customers_section')}</h2>
      {filteredCustomers.map(customer => (
        <div key={customer.id} className="user-card">
          <input value={customer.name} onChange={(e) => handleCustomerInputChange(customer.id, 'name', e.target.value)} />
          <input value={customer.email} onChange={(e) => handleCustomerInputChange(customer.id, 'email', e.target.value)} />
          <input value={customer.phone} onChange={(e) => handleCustomerInputChange(customer.id, 'phone', e.target.value)} />
          <input value={customer.organization} onChange={(e) => handleCustomerInputChange(customer.id, 'organization', e.target.value)} />
          <button className="submit-btn" onClick={() => submitCustomerChanges(customer.id)}>{t('save_changes')}</button>
        </div>
      ))}

      <h2>{t('admins_section')}</h2>
      {filteredAdmins.map(admin => (
        <div key={admin.id} className="user-card">
          <input value={admin.username} onChange={(e) => handleAdminInputChange(admin.id, 'username', e.target.value)} />
          <input value={admin.email} onChange={(e) => handleAdminInputChange(admin.id, 'email', e.target.value)} />
          <select value={admin.role} onChange={(e) => handleAdminInputChange(admin.id, 'role', e.target.value)}>
            <option value="owner">{t('owner')}</option>
            <option value="manager">{t('manager')}</option>
            <option value="worker">{t('worker')}</option>
            {admin.role === 'system_admin' && <option value="system_admin">{t('system_admin')}</option>}
          </select>
          <input placeholder={t('change_pin')} onChange={(e) => handleAdminInputChange(admin.id, 'pin', e.target.value)} />
          <input placeholder={t('location')} onChange={(e) => handleAdminInputChange(admin.id, 'location', e.target.value)} />
          <input type="password" placeholder={t('new_password')} onChange={(e) => handleAdminInputChange(admin.id, 'newPassword', e.target.value)} />
          <button className="submit-btn" onClick={() => submitAdminChanges(admin.id)}>{t('save_changes')}</button>
        </div>
      ))}
    </div>
  );
};

export default EditUsers;
